/**
 * OverviewPage — data-bound Overview tab (Plan 06 Task 6.5).
 *
 * Layout mirrors IA §2.1:
 *   - Row 1: KPI grid with 5 cards (activeUsers, requests, ratings,
 *     avgRating, timeSaved)
 *   - Row 2: Activity trend (2/3 width) + Rating donut (1/3 width)
 *   - Row 3: Recent feedback (2/3 width) + Stale status card (1/3 width)
 *
 * Each section is wrapped in a `<TabErrorBoundary>` so a render failure in
 * one widget doesn't take down the whole tab (IA §9.4). Loading states are
 * handled by the widgets themselves via `loading={isLoading}`; error states
 * render a small retry card inline with the widget.
 */

import { RecountNotice } from '@/components/RecountNotice';
import * as React from 'react';
import {
  useActivityTrend,
  useOverviewKpis,
  useRatingDistribution,
} from '@/hooks/use-overview';
import { useUrlFilters } from '@/hooks/use-url-filters';
import { resolveRange } from '@/lib/filters-schema';
import { effectiveGranularity } from '@/lib/granularity';
import { chartPalette } from '@/lib/chart-palette';
import { Donut, type DonutSegment } from '@/components/Donut';
import { KPICard } from '@/components/KPICard';
import { TabErrorBoundary } from '@/components/TabErrorBoundary';
import { TimeSavedChart } from '@/components/TimeSavedChart';
import { TimeSavedBreakdownModal } from '@/components/TimeSavedBreakdownModal';
import { TraceDrillDownModal } from '@/components/TraceDrillDownModal';
import { useTimeSavedSeries } from '@/hooks/use-time-saved-series';
import { TrendChart } from '@/components/TrendChart';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { InteractiveChartHint } from '@/components/charts/InteractiveChartHint';

/** Render a small inline retry card when a hook's `isError` flag fires. */
function ErrorRetry({
  label,
  onRetry,
}: {
  label: string;
  onRetry: () => void;
}): React.ReactElement {
  return (
    <Card data-testid={`${label}-error`}>
      <CardContent className="p-4 space-y-2">
        <div className="text-sm font-medium text-red-600">
          {label} failed to load
        </div>
        <button
          type="button"
          onClick={onRetry}
          className="text-xs underline text-primary hover:no-underline"
        >
          Retry
        </button>
      </CardContent>
    </Card>
  );
}

export function OverviewPage() {
  const { filters } = useUrlFilters();

  // Resolve the filter's preset/range into ISO dates so the granularity hint
  // matches what the server actually computes.
  const range = resolveRange(filters);
  const eff = effectiveGranularity(filters.granularity, range.from, range.to);

  const kpis = useOverviewKpis(filters);
  const trend = useActivityTrend(filters, filters.granularity);
  const dist = useRatingDistribution(filters);
  // bug F2 — the old "Recent feedback" row on Overview was removed; the same
  // stream lives on the Feedback tab where it belongs. `useRecentFeedback`
  // stays in use-overview.ts for other consumers / future reuse.
  // L1 — `useSyncStatus` is now consumed by DashboardLayout (gray text next
  // to the top-right stale dot); the Overview tile that used to show it was
  // removed because the signal is global, not Overview-specific.
  // Bug G5 — per-bucket time-saved series. Endpoint
  // `/salescoach/api/operations/time-saved-series` is NOT yet implemented (Agent I
  // task); the chart renders a "pending backend" notice on fetch failure
  // so the UI ships wired but blocked.
  const timeSavedSeries = useTimeSavedSeries(filters, filters.granularity);

  // v1.4 dashboard-ux-fix (Task 3) — per-bucket drill-down state. `null`
  // keeps the modal closed AND the `useTimeSavedBreakdown` hook inert
  // (enabled: false on the query) so we don't fire a stray fetch on page
  // load. The ISO string is what Recharts surfaces on bar click; we pass
  // it through unchanged so the backend's window-narrow matches exactly.
  const [selectedTimeSavedBucket, setSelectedTimeSavedBucket] =
    React.useState<string | null>(null);

  // Fall-back "null-ish" rows keep the KPI grid structurally stable
  // regardless of query state — callers read from `kpis.data?.<field>`
  // and KPICard handles null/loading props.
  const k = kpis.data;

  // Time-saved KPI companion: time saved per active user. The mean (total ÷
  // active users) rides as the visible sub-label; the median — robust to the
  // heavy right tail of power users — lives in the tooltip. Both are null when
  // there are no active users, in which case we drop the sub-label entirely.
  const fmtHours = (h: number | null | undefined): string =>
    h === null || h === undefined ? '—' : `${h.toFixed(1)}h`;
  // Optional-chain through `timeSavedPerUser` too: an older dashboard API (or
  // one mid-deploy) returns OverviewKpis WITHOUT this field, so `k` can be
  // present while `k.timeSavedPerUser` is undefined — guard both.
  const timeSavedMean = k?.timeSavedPerUser?.mean ?? null;
  const timeSavedSubLabel =
    timeSavedMean !== null ? `avg ${timeSavedMean.toFixed(1)}h/user` : undefined;
  const timeSavedTooltip =
    'Hours saved — matches /report Slack command. Per-trace minutes (reactive simple 1 / medium 10 / complex 20, brief 10 daily / 20 weekly) × rating multiplier (5→1.0, 4→0.7, 3→0.3, 1–2→0, reactive unrated→0.3, brief unrated→0), plus flat +5 for Jira-prefill opens and +15 for KB article publishes.' +
    (k?.timeSavedPerUser
      ? ` Per active user this window: average ${fmtHours(k.timeSavedPerUser.mean)} — the total above ÷ active users (so it includes scheduled-brief credit). Median ${fmtHours(k.timeSavedPerUser.median)} — the typical active user's own reactive time saved (half saved more, half less). The average sits higher: it spreads the full total across active users and is pulled up by a few heavy users.`
      : '');

  // Rating distribution → Donut segments. Hide the 0-label star rows so the
  // donut doesn't render razor-thin slivers.
  // 2026-04-24 (revised): only the 5★ slice is pinned to the RM-badge green
  // (`chartPalette.rmBadge` = #16a34a); 1★-4★ fall through to the Donut's
  // DEFAULT_PALETTE diverging ramp (red → orange → amber → lime). Earlier
  // iteration painted every slice green — the user clarified that only the
  // top rating should carry the RM-green accent, matching the
  // RatingDistribution histogram behaviour.
  const donutData = (dist.data ?? []).map((b) => ({
    label: `${b.rating}★`,
    value: b.count,
    ...(b.rating === 5 ? { color: chartPalette.rmBadge } : {}),
  }));
  const rated = donutData.reduce((s, d) => s + d.value, 0);
  const avg = k?.avgRating.current ?? null;
  // Clicking a donut segment opens the TraceDrillDownModal scoped to that
  // rating (v1.4 UX fix). Local state drives both the donut's highlight
  // (`selectedLabel`) and the modal's `open`, so toggling off (clicking the
  // same segment) clears both without a second piece of state.
  const [drillDownRating, setDrillDownRating] = React.useState<
    1 | 2 | 3 | 4 | 5 | null
  >(null);
  const handleDonutSegmentClick = React.useCallback(
    (segment: DonutSegment | null) => {
      if (!segment) {
        setDrillDownRating(null);
        return;
      }
      const match = segment.label.match(/^([1-5])★$/);
      if (match) {
        setDrillDownRating(Number(match[1]) as 1 | 2 | 3 | 4 | 5);
      }
    },
    [],
  );
  const selectedSegment = React.useMemo<DonutSegment | null>(() => {
    if (drillDownRating === null) return null;
    return donutData.find((s) => s.label === `${drillDownRating}★`) ?? null;
  }, [drillDownRating, donutData]);
  // Two-line layout so the label fits inside the donut hole at the
  // 1280px-viewport column width. The single-line variant (e.g.
  // "4.0 avg (288 rated)") was ~140px wide at text-sm, which overflowed
  // the ~155px-wide hole when the donut sits in xl:grid-cols-3. Splitting
  // into "4.0 avg" + "288 rated" (and "5★" + "120 (42%)" for the
  // selected-segment branch) keeps the label inside the hole at every
  // breakpoint we ship.
  const centerLabel = React.useMemo<React.ReactNode>(() => {
    if (selectedSegment) {
      const share =
        rated > 0 ? (selectedSegment.value / rated) * 100 : 0;
      return (
        <div className="flex flex-col items-center gap-0.5 leading-tight">
          <span className="text-base font-semibold tabular-nums">
            {selectedSegment.label}
          </span>
          <span className="text-xs text-muted-foreground tabular-nums">
            {selectedSegment.value} ({share.toFixed(1)}%)
          </span>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center gap-0.5 leading-tight">
        {avg !== null && (
          <span className="text-base font-semibold tabular-nums">
            {avg.toFixed(1)} avg
          </span>
        )}
        <span className="text-xs text-muted-foreground tabular-nums">
          {rated} rated
        </span>
      </div>
    );
  }, [selectedSegment, avg, rated]);

  // TODO(audit): P2-4 — the page title briefly underlaps the sticky header's
  // backdrop-blur strip while scrolling. Both fix options (extra top padding
  // on <main>, or an opaque sticky chrome background) live in
  // DashboardLayout.tsx, outside this page's ownership. Track with the
  // layout agent.
  // TODO(audit): P3-3 — the Donut center-label swaps without a transition;
  // the fix belongs in the shared <Donut> component (components/Donut.tsx),
  // not here.
  return (
    <section aria-labelledby="overview-heading" className="space-y-6">
      <header>
        <h1 id="overview-heading" className="text-2xl font-semibold">
          Overview
        </h1>
        <p className="text-sm text-muted-foreground">
          Snapshot for the selected filters.
        </p>
        <RecountNotice queries={[kpis, trend, dist, timeSavedSeries]} />
      </header>

      {/* Row 1 — KPI grid (IA §2.2.1). 5 cards on desktop. */}
      <TabErrorBoundary name="KPI row">
        {kpis.isError ? (
          <ErrorRetry label="KPIs" onRetry={() => void kpis.refetch()} />
        ) : (
          // P2-1: 5 cards in a 2-col mobile grid stranded the 5th card on
          // its own row. Making the 5th tile span two columns on mobile
          // keeps the row balanced — 2 | 2 | [full-width] — and falls
          // back to the normal 5-across layout at xl where all five tiles
          // fit in one row. On the md breakpoint (tablet, still 2 cols)
          // the same col-span-2 rule keeps Time saved full-width so it
          // doesn't orphan-row there either.
          <div className="grid grid-cols-2 md:grid-cols-2 xl:grid-cols-5 gap-3 md:gap-4">
            <KPICard
              label="Active users"
              value={k?.activeUsers.current ?? null}
              delta={k?.activeUsers.delta ?? null}
              deltaCurrent={k?.activeUsers.current ?? null}
              deltaPrevious={k?.activeUsers.previous ?? null}
              sampleSize={k?.activeUsers.current ?? undefined}
              loading={kpis.isLoading}
              tooltip="Distinct users (username when known, else slackId) with ≥1 non-scheduled Mr. Edge trace in the selected window. Scheduled briefs excluded."
            />
            <KPICard
              label="Requests"
              value={k?.requests.current ?? null}
              delta={k?.requests.delta ?? null}
              deltaCurrent={k?.requests.current ?? null}
              deltaPrevious={k?.requests.previous ?? null}
              sampleSize={k?.requests.current ?? undefined}
              loading={kpis.isLoading}
              tooltip="Count of non-scheduled traces in the window (user DM + @mention + side events like article publishes and Jira-prefill clicks). Scheduled briefs not counted."
            />
            <KPICard
              label="Ratings"
              value={k?.ratings.current ?? null}
              delta={k?.ratings.delta ?? null}
              deltaCurrent={k?.ratings.current ?? null}
              deltaPrevious={k?.ratings.previous ?? null}
              sampleSize={k?.ratings.current ?? undefined}
              subLabel={
                k
                  ? (() => {
                      const req = k.requests.current;
                      const share =
                        req > 0 ? (k.ratings.current / req) * 100 : null;
                      const sharePart =
                        share !== null ? ` · ${share.toFixed(0)}% of requests` : '';
                      return `${k.ratings.current}${sharePart} · ${k.ratings.withText} w/ text`;
                    })()
                  : undefined
              }
              loading={kpis.isLoading}
              tooltip="Number of traces with a 1–5 star rating (reactive + briefs). 'w/ text' = ratings that also carry a free-form comment."
            />
            <KPICard
              label="Avg rating"
              value={k?.avgRating.current ?? null}
              format="rating"
              delta={k?.avgRating.delta ?? null}
              deltaCurrent={k?.avgRating.current ?? null}
              deltaPrevious={k?.avgRating.previous ?? null}
              // Sample size for the avg is the number of ratings that
              // contributed (not the avg value itself). Small-N muting
              // triggers when fewer than 3 ratings anchor the mean.
              sampleSize={k?.ratings.current ?? undefined}
              // Rating delta is a ratio of fractional means — 10 as the
              // minPreviousSampleForDelta (count default) is over-strict.
              // 3 matches the small-N threshold used elsewhere for means.
              minPreviousSampleForDelta={3}
              loading={kpis.isLoading}
              tooltip="Mean star rating (1–5) across all rated traces in the window. Unrated traces excluded."
            />
            <KPICard
              className="col-span-2 md:col-span-2 xl:col-span-1"
              label="Time saved"
              value={k?.timeSaved.current ?? null}
              format="hours"
              delta={k?.timeSaved.delta ?? null}
              deltaCurrent={k?.timeSaved.current ?? null}
              deltaPrevious={k?.timeSaved.previous ?? null}
              // No sampleSize: time-saved is a cumulative sum, not an
              // n-anchored mean — muting on "low hours" would be noise.
              loading={kpis.isLoading}
              // Sub-label: mean time saved per active user (total ÷ active),
              // computed over the SAME selected window as the headline total.
              // Median (heavy-tail-robust) rides in the tooltip.
              subLabel={timeSavedSubLabel}
              tooltip={timeSavedTooltip}
            />
          </div>
        )}
      </TabErrorBoundary>

      {/* Row 1.5 — Time saved per bucket (bug G5 / new widget).
          Placed directly below the KPI row so the per-period time-saved
          pattern sits next to the aggregate "Time saved" KPI card. */}
      <TabErrorBoundary name="Time saved">
        <Card>
          <CardContent className="p-4">
            <TimeSavedChart
              data={timeSavedSeries.data}
              loading={timeSavedSeries.isLoading}
              error={
                timeSavedSeries.isError
                  ? 'Failed to load time-saved series — see console.'
                  : null
              }
              onBucketClick={setSelectedTimeSavedBucket}
            />
          </CardContent>
        </Card>
      </TabErrorBoundary>
      {/* Task 3 — per-bucket drill-down modal. Rendered once at the page
          level (not per-chart) so the open/close state stays in one place
          and a bucket click from any future chart variant can reuse the
          same modal. The hook inside is inert when `bucket === null`. */}
      <TimeSavedBreakdownModal
        bucket={selectedTimeSavedBucket}
        granularity={filters.granularity}
        filters={filters}
        onClose={() => setSelectedTimeSavedBucket(null)}
      />

      {/* v1.4 dashboard-ux-fix: Rating donut drill-down. Modal is inert
          (hook `enabled: false`) until `drillDownRating !== null`, so no
          spurious fetch fires on first paint. */}
      <TraceDrillDownModal
        open={drillDownRating !== null}
        onClose={() => setDrillDownRating(null)}
        filters={filters}
        extras={drillDownRating !== null ? { rating: drillDownRating } : {}}
        title={
          drillDownRating !== null
            ? `Traces rated ${drillDownRating}★`
            : ''
        }
        subtitle="Every trace at this rating for the current filters."
        testIdPrefix="overview-rating-drilldown"
      />

      {/* Row 2 — Trend chart (2/3) + Donut (1/3).
          L1 — Sync status moved to the global top-right chrome, so the right
          column is now just the Donut taking the full 1/3. The previous
          stacked layout (Donut + StaleStatusCard) is gone; the Donut card
          still sits on its natural height so the grid doesn't feel empty.
          O1 — DAU-trend tile removed per user request: the Users trend
          (new/returning breakdown) on Engagement already covers DAU with
          more signal, and the Activity trend below shows request volume. */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 space-y-4">
          <TabErrorBoundary name="Activity trend">
            {trend.isError ? (
              <ErrorRetry
                label="Activity trend"
                onRetry={() => void trend.refetch()}
              />
            ) : (
              <Card>
                <CardContent className="p-4">
                  {/* `dayFilter` is the MODE, not the derived boolean. This used to pass
                      `filters.includeWeekends`, which `filters-schema.ts` computes as
                      `dayFilter === 'all'` — it cannot tell 'weekdays' from 'weekends', so
                      under "Weekends only" the padder deleted every bucket the server
                      returned and drew synthetic Mon–Fri zeros, while the `badge` below
                      summed the UNPADDED series and printed the real total on the same
                      header. */}
                  <TrendChart
                    data={trend.data ?? []}
                    loading={trend.isLoading}
                    granularity={eff}
                    title="Requests & Active Users"
                    dayFilter={filters.dayFilter}
                    badge={(() => {
                      // Overview v1.4.1: mirror the TimeSavedChart header
                      // format — "RM: X · Non-RM: Y · Total: Z" — so the two
                      // Overview timelines speak the same vocabulary. Uses
                      // the backend RM/Non-RM split (rmRequests/nonRmRequests).
                      // Bypass when loading / empty so the badge doesn't read
                      // "0 / 0 / 0" on first paint.
                      const rows = trend.data ?? [];
                      if (trend.isLoading || rows.length === 0) return null;
                      const safe = (v: unknown): number =>
                        typeof v === 'number' && Number.isFinite(v) ? v : 0;
                      const totalRm = rows.reduce(
                        (s, r) => s + safe(r.rmRequests),
                        0,
                      );
                      const totalNonRm = rows.reduce(
                        (s, r) => s + safe(r.nonRmRequests),
                        0,
                      );
                      const totalRequests = rows.reduce(
                        (s, r) => s + safe(r.requests),
                        0,
                      );
                      return (
                        <>
                          <span style={{ color: chartPalette.rmRequests }}>RM</span>:{' '}
                          <span className="font-semibold text-foreground">
                            {totalRm.toLocaleString()}
                          </span>{' '}
                          · Non-RM:{' '}
                          <span className="font-semibold text-foreground">
                            {totalNonRm.toLocaleString()}
                          </span>{' '}
                          · Total:{' '}
                          <span className="font-semibold text-foreground">
                            {totalRequests.toLocaleString()}
                          </span>
                        </>
                      );
                    })()}
                  />
                </CardContent>
              </Card>
            )}
          </TabErrorBoundary>
        </div>
        <div>
          <TabErrorBoundary name="Rating distribution">
            {dist.isError ? (
              <ErrorRetry
                label="Rating distribution"
                onRetry={() => void dist.refetch()}
              />
            ) : (
              <Card>
                <CardHeader className="p-4 pb-0 flex flex-row items-center justify-between gap-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Rating distribution
                  </CardTitle>
                  <InteractiveChartHint data-testid="overview-donut-hint" />
                </CardHeader>
                <CardContent className="p-4">
                  <Donut
                    data={donutData}
                    /* The centre label counts `donutData`, so with nothing
                       counted yet it reads "0 rated" — a real-looking count
                       of a window nobody has measured. `loading` replaces the
                       whole chart, label included. */
                    loading={dist.isLoading}
                    /* 1★..5★ — a fixed set, so the legend column is known
                       before the counts are. */
                    legendRows={5}
                    centerLabel={centerLabel}
                    onSegmentClick={handleDonutSegmentClick}
                    selectedLabel={selectedSegment?.label ?? null}
                  />
                </CardContent>
              </Card>
            )}
          </TabErrorBoundary>
        </div>
      </div>
    </section>
  );
}
